import type { ReactNode } from 'react';

type BmiTier = 'underweight' | 'normal' | 'overweight' | 'obese';
type ObeseClass = 1 | 2 | 3;

interface ModalBlock {
  label?: string;
  text?: string;
  content?: ReactNode;
  urgent?: boolean;
}

const OBESE_CLASS_TEXT: Record<ObeseClass, string> = {
  1: 'Hafif Obezite. Sağlık riskleri artmaya başlamıştır.',
  2: 'Orta Derece Obezite. Tedavi planı için uzman desteği kritiktir.',
  3: 'İleri Derece Obezite. Acil tıbbi değerlendirme gereklidir.',
};

const SYSTEMIC_WARNING =
  'Obezite sadece bir kilo sorunu değil; kardiyovasküler sistem başta olmak üzere, endokrin (diyabet), iskelet-kas sistemi, solunum ve sindirim sistemlerini doğrudan riske atan kronik bir hastalıktır.';

const MANDATORY_ACTION_EVRE_1_2 =
  'Obezite tanısı konulmuştur; multidisipliner destek (Diyetisyen, Dahiliye) önerilir. Kalıcı ve sağlıklı çözüm için bir endokrinoloji uzmanı, iç hastalıkları doktoru ve diyetisyen eşliğinde multidisipliner bir yaklaşım şarttır.';

const MORBID_URGENT_MESSAGE =
  "Mevcut vücut kitle indeksiniz hayati risk taşıyan 'Morbid Obezite' sınırındadır. Bu tablo; ani kalp durması, ileri derece uyku apnesi ve tip 2 diyabet gibi yaşamı tehdit eden komplikasyonlar için çok yüksek risk içerir.";

const MORBID_SURGICAL_ACTION = (
  <>
    Yaşam tarzı değişikliklerinin ötesinde, <strong>acil</strong> tıbbi müdahale ve değerlendirme gereklidir. Tedavi seçenekleri ve metabolik süreçlerin yönetimi için bir Endokrinoloji Uzmanı ile birlikte{' '}
    <strong>Genel Cerrahi (Obezite Cerrahisi)</strong> bölümüne başvurmanız hayati önem taşımaktadır.
  </>
);

function getModalContent(
  tier: BmiTier,
  obeseClass?: ObeseClass
): {
  title: string;
  titleUrgent?: boolean;
  titleMorbid?: boolean;
  modalMorbid?: boolean;
  blocks: ModalBlock[];
} {
  switch (tier) {
    case 'underweight':
      return {
        title: 'Yaşam Tarzı Önerileri',
        blocks: [
          { label: 'Uyarı', text: 'Vücut kitle indeksiniz ideal sınırın altındadır.' },
          {
            label: 'Öneri',
            text: 'Yetersiz beslenme, altta yatan hormonal nedenler veya metabolik durumların değerlendirilmesi için bir sağlık uzmanına veya diyetisyene başvurmanız önerilir.',
          },
        ],
      };
    case 'normal':
      return {
        title: 'Yaşam Tarzı Önerileri',
        blocks: [
          {
            text: "Tebrikler! Mevcut kilonuz sağlıklı aralıktadır. Dengeli beslenme ve aktif yaşam tarzı ile bu tabloyu korumaya devam edin.",
          },
        ],
      };
    case 'overweight':
      return {
        title: 'Yaşam Tarzı Önerileri',
        blocks: [
          {
            label: 'Uyarı',
            text: "Mevcut kilonuz ideal sınırın üzerindedir. Bu durum ilerleyen dönemde obezite ve ilişkili kronik hastalıklar için risk oluşturabilir.",
          },
          {
            label: 'Öneri',
            text: "Yaşam tarzı değişiklikleri ve fiziksel aktivite artışı bu riski yönetmenize yardımcı olacaktır.",
          },
        ],
      };
    case 'obese': {
      const isMorbid = obeseClass === 3;
      const classText = obeseClass ? OBESE_CLASS_TEXT[obeseClass] : OBESE_CLASS_TEXT[1];
      if (isMorbid) {
        return {
          title: 'KRİTİK UYARI: İLERİ DERECE (MORBİD) OBEZİTE',
          titleUrgent: true,
          titleMorbid: true,
          modalMorbid: true,
          blocks: [
            { label: 'Acil Durum', text: MORBID_URGENT_MESSAGE, urgent: true },
            { label: 'Cerrahi Öneri', content: MORBID_SURGICAL_ACTION, urgent: true },
          ],
        };
      }
      return {
        title: 'Yaşam Tarzı Önerileri',
        titleUrgent: true,
        blocks: [
          { label: 'TEMD Evreleme', text: classText },
          { label: 'Sistemik Uyarı', text: SYSTEMIC_WARNING, urgent: true },
          { label: 'Zorunlu Öneri', text: MANDATORY_ACTION_EVRE_1_2, urgent: true },
        ],
      };
    }
  }
}

interface BmiSuggestionsModalProps {
  visible: boolean;
  tier: BmiTier;
  obeseClass?: ObeseClass;
  onClose: () => void;
}

export function BmiSuggestionsModal({ visible, tier, obeseClass, onClose }: BmiSuggestionsModalProps) {
  if (!visible) return null;

  const content = getModalContent(tier, obeseClass);

  return (
    <div
      className="animate-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bmi-suggestions-title"
      onClick={onClose}
    >
      <div
        className={`animate-modal-content flex max-h-[90vh] w-full max-w-md flex-col rounded-3xl bg-white shadow-2xl ${
          content.modalMorbid ? 'border-4 border-red-800' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex shrink-0 items-start justify-between gap-4 border-b px-6 py-4 ${
            content.titleMorbid ? 'border-red-200 bg-red-900 text-white' : 'border-slate-100'
          }`}
        >
          <h2
            id="bmi-suggestions-title"
            className={`text-lg font-bold ${
              content.titleMorbid ? 'text-white' : content.titleUrgent ? 'text-red-600' : 'text-text-heading'
            }`}
          >
            {content.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={`-m-2 rounded-lg p-2 transition ${
              content.titleMorbid ? 'text-red-200 hover:bg-red-800 hover:text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            }`}
            aria-label="Kapat"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {content.blocks.map((block, i) => (
              <div key={i}>
                {block.label && (
                  <p
                    className={`mb-1 text-sm font-semibold ${
                      block.urgent
                        ? 'text-red-800'
                        : tier === 'obese'
                          ? 'text-red-600'
                          : tier === 'overweight'
                            ? 'text-orange-600'
                            : tier === 'underweight'
                              ? 'text-amber-600'
                              : 'text-text-heading'
                    }`}
                  >
                    {block.label}:
                  </p>
                )}
                {block.content ? (
                  <p
                    className={`text-[15px] leading-relaxed ${
                      block.urgent ? 'font-semibold text-red-900' : 'text-text-heading'
                    }`}
                  >
                    {block.content}
                  </p>
                ) : (
                  <p
                    className={`text-[15px] leading-relaxed ${
                      block.urgent ? 'font-semibold text-red-900' : 'text-text-heading'
                    }`}
                  >
                    {block.text}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="shrink-0 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl bg-primary py-3 text-base font-bold text-white transition hover:bg-primary-active active:bg-primary-active"
          >
            Anladım
          </button>
        </div>
      </div>
    </div>
  );
}
